import React from 'react';
import {
	useIntl,
} from 'react-intl';

import ChartContainer from '~/components/atoms/charts/components/ChartContainer';
import ColumnChart from '~/components/atoms/charts/charts/ColumnChart';

import {
	calculateTickIntervalNew,
} from '~/model/websiteStatistics/charts/utilities';



type Props<Value extends string | number | symbol | boolean> = {
	categories?: Array<{
		value: Value;
		label: string;
		color: string;
	}>;
	data?: Array<{
		count: number;
		percentage: number;
		value: Value;
	}>;
	name: string;
	numberOfYAxisPoints?: number;
};

function BarPagesChart<Value extends string | number | symbol | boolean>(props: Props<Value>) {
	const {
		categories,
		data = [],
		name,
		numberOfYAxisPoints = 4,
	} = props;

	const intl = useIntl();

	const renderTooltip = React.useCallback(
		({ count, index }) => {
			const item = data[index];
			if (!item) {
				return '';
			}

			const category = categories?.find((cat) => cat.value === item.value);
			const label = category?.label ?? String(item.value);

			const percentage = intl.formatNumber(
				item.percentage / 100,
				{
					style: 'percent',
					maximumFractionDigits: 2,
					maximumSignificantDigits: 4,
				},
			);

			return label + ': ' + intl.formatNumber(count) + ' (' + percentage + ')';
		},
		[
			data,
			intl,
			categories,
		],
	);

	const renderableData = data.length > 0 ? data
		.filter((item) => item.count >= 0)
		.sort((itemA, itemB) => {
			if (categories) {
				const positionA = categories.findIndex((cat) => cat.value === itemA.value);
				const positionB = categories.findIndex((cat) => cat.value === itemB.value);

				if (positionA === positionB) {
					return 0;
				}

				return positionA < positionB ? -1 : 1;
			}

			return 0;
		}) : [{ count: 0, percentage: 0, value: '-' }];

	const seriesColors = renderableData.map((item) => {
		const category = categories?.find((cat) => cat.value === item.value);
		return category?.color ?? 'grey';
	})

	const xAxisCaptions = renderableData.map((item) => {
		const category = categories?.find((cat) => cat.value === item.value);
		return category?.label ?? String(item.value);
	})

	return (
		<ChartContainer
			chart={(
				<ColumnChart
					animate={true}
					barWidth={15}
					data={[
						{
							name: 'foo',
							data: renderableData.map((item) => item.count),
						},
					]}
					key={name}
					maximum={({ maxValue }) => calculateTickIntervalNew(maxValue * 1.005, numberOfYAxisPoints)}
					minimum={0}
					renderTooltipCallback={renderTooltip}
					seriesColors={seriesColors}
					xAxisCaptions={xAxisCaptions}
					yAxisPoints={numberOfYAxisPoints}
				/>
			)}
			name={name}
			type="column-chart"
		/>
	);
}



export default React.memo(BarPagesChart);
